import type { BusinessUnitListItem } from '@clockwise/clients/business-units';
import { ApiError } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { BusinessUnitFormSheet } from '@/components/custom/business-unit-form-sheet';
import { NoAccess } from '@/components/custom/no-access';
import { RowActions } from '@/components/custom/row-actions';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { businessUnitsApi } from '@/lib/api';
import { useCanCreateUnits, useCanManageUnits } from '@/lib/business-units';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/setup/business-units/')({
  component: BusinessUnitsPage,
});

/**
 * Setup → Business units: the companies inside the organisation the caller
 * works in (every unit for a Super Admin), with what each holds. A row opens
 * the unit's page. A Super Admin adds and edits units here, in a side sheet.
 */
function BusinessUnitsPage() {
  const canView = useHasPermission('business_unit.view');
  const canManageUnits = useCanManageUnits();
  const canCreateUnits = useCanCreateUnits();
  const navigate = useNavigate();
  // `undefined` = closed; `null` = adding; a unit = editing it.
  const [editing, setEditing] = useState<BusinessUnitListItem | null | undefined>(undefined);
  const units = useQuery({
    queryKey: queryKeys.businessUnits.list(),
    queryFn: () => businessUnitsApi.list(),
    enabled: canView,
  });

  if (!canView) return <NoAccess permission="business_unit.view" resource="Business Units" />;
  const rows = units.data?.data ?? [];

  return (
    <PageShell>
      <PageHeader
        title="Business Units"
        subtitle="The companies inside your organisation. Each has its own customers, employees and roles."
        count={rows.length}
      >
        {canCreateUnits ? (
          <Button onClick={() => setEditing(null)}>
            <Plus className="size-4" />
            New Business Unit
          </Button>
        ) : null}
      </PageHeader>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead className="text-right">Employees</TableHead>
              <TableHead className="text-right">Customers</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {units.isLoading ? (
              [0, 1].map((i) => (
                <TableRow key={i}>
                  {[0, 1, 2, 3, 4, 5].map((j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : units.isError ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-destructive">
                  {units.error instanceof ApiError
                    ? units.error.message
                    : 'Failed to load Business Units.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((u) => (
                <TableRow
                  key={u.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({ to: '/setup/business-units/$id', params: { id: u.id } })
                  }
                >
                  <TableCell>
                    <Link
                      to="/setup/business-units/$id"
                      params={{ id: u.id }}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {u.name}
                    </Link>
                    {u.isDefault ? (
                      <Badge variant="outline" className="ml-2">
                        Original unit
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{u.code}</TableCell>
                  <TableCell className="text-right">
                    {u.counts.employees.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {u.counts.customers.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">Active</Badge>
                  </TableCell>
                  <TableCell className="w-12">
                    {canManageUnits ? (
                      <RowActions>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(u);
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </RowActions>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <BusinessUnitFormSheet
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        unit={editing ?? null}
      />
    </PageShell>
  );
}
